import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ReactHtmlParser from '@orrisroot/react-html-parser';

import { setPageModel, setRenewalTermsAccepted, setUseDefaultStepper } from 'actions/expiryRenewalActions';
import infoIcon from 'assets/images/button/info-blue.svg';
import Spinner from 'components/Spinner';
import { ELIGIBLE } from 'constants/policyInfoConstants';
import { DOCUMENTS, RAC_LOYALTY_REWARDS, RENEWAL_ERROR, RENEWAL_CUSTOMISE, RENEWAL_SUMMARY, ROOT } from 'constants/routeConstants';
import useCosmosTrackEvent from 'hooks/useCosmosTrackEvent';
import useReferrerParam from 'hooks/useReferrerParam';
import { getRenewalDetails } from 'services/RenewalService';
import { ResponseCode } from 'services/ContentService';
import Button from 'shared/components/Button/Button';
import { dispatch } from 'store';
import { CoverItem, PaidUpgrade } from 'types/expiryRenewal';
import { Store } from 'types/store';
import mobileAppEvent from 'utils/reactNativeWebView';
import useViewport from 'utils/useViewportHelper';
import Footer from 'views/MyRac/ExpiryRenewal/components/atoms/Footer';

import Banner from './components/molecules/Banner';
import CheckedList from './components/molecules/CheckedList';
import RenewalTermsModal from './components/molecules/RenewalTermsModal';
import FlexToggle from './components/molecules/FlexToggle';
import ViewBasket from './components/molecules/ViewBasket';
import Stepper from './components/organisms/Stepper';
import useOneMonthFreeSearchParam from './hooks/useOneMonthFreeSearchParam';

const Renewal: React.FC = () => {
  const { pageModel, renewalTermsAccepted, useDefaultStepper } = useSelector((state: Store) => state.expiryRenewal);

  const [loading, setLoading] = useState(!pageModel);
  const oneMonthFreeSearchParam = useOneMonthFreeSearchParam();

  const navigate = useNavigate();

  const isAppView: boolean = useReferrerParam();

  const { isMobile } = useViewport();

  const { cosmosTrackEvent } = useCosmosTrackEvent('ExpiryRenewals');

  // Track which paid upgrades are displayed to the member on the PDP
  const trackUpgradesDisplayed = (paidUpgrades: PaidUpgrade[] | undefined) => {
    paidUpgrades?.forEach((upgrade) => {
      const productCode = upgrade?.productCode ?? '';
      if (productCode === 'AtHomeRenewal') {
        cosmosTrackEvent('upgrade_displayed_athome');
      } else if (productCode === 'OnwardTravelRenewal') {
        cosmosTrackEvent('upgrade_displayed_onwardtravel');
      }
    });
  };

  // What's Included
  const listItems = (list: CoverItem[]) => list.map((item) => item && (
    <CheckedList key={item?.header} header={item?.header} body={item?.body} modal={item?.modal} />
  ));

  const fetchData = async () => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const res: any = await getRenewalDetails(oneMonthFreeSearchParam);

      if (res?.payload && res?.responseCode === ResponseCode.SUCCESS) {
        const { renewalJourneyDetails, hasRlrJourneyDetails, rlrJourneyDetails } = res.payload || {};
        // Expiry members redirected with RLR payload in state to the loyalty pdp as non-continuous journey so they must choose RLR before returning to renew
        if (hasRlrJourneyDetails && rlrJourneyDetails) {
          navigate(RAC_LOYALTY_REWARDS, { state: { rlrJourneyDetails }, replace: true });
          return;
        }
        // Renewal PDP
        if (renewalJourneyDetails) {
          if (renewalJourneyDetails?.eligibility === ELIGIBLE) {
            // Redux store
            dispatch(setPageModel(renewalJourneyDetails));
            // Customise step is available when there are paid upgrades, or the user can add/edit vehicles or members
            const canAddVehicle = !!renewalJourneyDetails.isVehicleAddAllowed
              && (renewalJourneyDetails.extraVehicleDetails?.maxNumberOfVehicleAdds ?? 0) > 0;
            const canAddMember = !!renewalJourneyDetails.isMemberAddAllowed
              && (renewalJourneyDetails.extraPersonDetails?.maxNumberOfPeopleAdds ?? 0) > 0;
            const hasCustomiseContent = (renewalJourneyDetails.paidUpgrades?.length ?? 0) > 0
              || canAddVehicle
              || !!renewalJourneyDetails.isVehicleEditAllowed
              || canAddMember
              || !!renewalJourneyDetails.isMemberEditAllowed;
            dispatch(setUseDefaultStepper(!hasCustomiseContent));
            // Send monthly active user data to cosmos db
            cosmosTrackEvent('renewal_journey_started');
            // Track paid upgrades displayed on the PDP
            trackUpgradesDisplayed(renewalJourneyDetails?.paidUpgrades);
          } else {
            // Error page
            navigate(RENEWAL_ERROR, { state: { isPaymentError: false, eligibility: renewalJourneyDetails?.eligibility }, replace: true });
          }
        }
      }
      setLoading(false);
    } catch (err) {
      // console.error(err);
      // Error page
      navigate(RENEWAL_ERROR, { state: { isPaymentError: false }, replace: true });
    }
  };

  useEffect(() => {
    // Make the initial api call to fetch renewal data once, on landing.
    // (Depending on pageModel here caused a refetch loop: fetchData dispatches
    // setPageModel, which returns a new pageModel object each time, re-triggering the effect.)
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="d-flex justify-content-center bg-grey">
        {isMobile ? <Spinner mobileSpinner /> : <Spinner bootSpinner />}
      </div>
    );
  }

  return (
    <>
      <RenewalTermsModal
        show={!!pageModel && !renewalTermsAccepted}
        onContinue={() => dispatch(setRenewalTermsAccepted(true))}
        onClose={() => {
          if (isAppView) {
            mobileAppEvent('RENEWAL_COMPLETED_CLOSE');
          } else {
            navigate(ROOT);
          }
        }}
      />
      {(pageModel?.discountModel?.isDiscountedPrice || pageModel?.isEligibleForOneMonthFreeDiscount) && (
        <Banner />
      )}
      <Stepper step={1} />
      <div className="p-3 fs-6">
        {/* Expiry Date */}
        <div className="card border rounded">
          <div className="card-body pb-0">
            <h2>Renew your breakdown cover</h2>
            <p>
              Thanks for being an RAC member.&nbsp;
              {pageModel?.discountModel?.basketDiscountText
                ? ReactHtmlParser(`<b>Save ${pageModel?.discountModel?.basketDiscountText}</b> on your cover when you renew today.`)
                : 'Renew today to keep access to your breakdown cover.'}
            </p>
            <p><b>Cover expires: {new Date(pageModel?.coverEndDate ?? '').toLocaleDateString('en-GB')}</b></p>
          </div>
        </div>
        {/* Cover Details */}
        <div className="card border-start border-5 border-orange border-0 rounded-0 shadow-sm p-3 mt-3">
          <div>Your cover details</div>
          <div className="fs-5 fw-bold">{pageModel?.coverTypeTitle}</div>
          <p>Your vehicle is covered, no matter who&apos;s driving</p>
          <div className="d-inline-flex mb-3">
            <span className="badge rounded-pill bg-orange text-white text-uppercase px-3">
              <small>{pageModel?.coverBasis}</small>
            </span>
          </div>
          {pageModel?.members && pageModel?.members?.length > 0 && (
            <>
              <div className="fw-bold">Covered members</div>
              <div>Your membership covers {pageModel?.members?.length} member{(pageModel?.members?.length ?? 0) > 1 && 's'}</div>
              <ul className="py-2">
                {pageModel?.members?.map((item) => (
                  <li key={`${item.firstName} ${item.surname}`}>
                    {item.firstName} {item.surname}
                  </li>
                ))}
              </ul>
            </>
          )}
          {pageModel?.vehicles && pageModel?.vehicles?.length > 0 && (
            <>
              <div className="fw-bold">Covered vehicles</div>
              <div>You&apos;re covered for {pageModel?.vehicles?.length} vehicle{pageModel?.vehicles?.length > 1 && 's'}</div>
              <div className="d-block mt-2 mb-4">
                {pageModel?.vehicles?.map((vehicle) => (
                  <React.Fragment key={vehicle.Registration}>
                    <span className="regPlate">{vehicle.Registration}</span>
                    <span className="mx-1" />
                  </React.Fragment>
                ))}
              </div>
            </>
          )}
          {/* Covered Items */}
          {pageModel?.yourCoverContent && pageModel?.yourCoverContent?.length > 0 && (
            listItems(pageModel?.yourCoverContent?.slice(1) ?? [])
          )}
          {/* Included Add Ons */}
          {pageModel?.addonsContent && pageModel?.addonsContent?.length > 0 && (
            listItems(pageModel?.addonsContent ?? [])
          )}
          <div className="lh-lg">
            <img src={infoIcon} alt="info" height="16" width="16" />
            <span onClick={() => navigate(DOCUMENTS)} className="mx-2 text-link" aria-hidden="true">
              See your policy details and documents
            </span>
          </div>
        </div>
      </div>
      {/* Basket */}
      <div className="card border fluid rounded-0 mt-2 p-3">
        <FlexToggle />
        <ViewBasket />
        <Button
          onClickHandler={() => navigate(useDefaultStepper ? RENEWAL_SUMMARY : RENEWAL_CUSTOMISE)}
          buttonText="Continue"
        />
      </div>
      <Footer />
    </>
  );
};

export default Renewal;
